import React, { useEffect, useState } from 'react';
import { BrowserView } from 'react-device-detect';
import ReactGA from 'react-ga4';
import { useNavigate } from 'react-router';
import { Link } from 'react-router-dom';
import { number, object, string } from 'yup';

import { useAlerta } from 'Component/Alerta';
import Botao from 'Component/Botao';
import Form, { Campo, Selecao } from 'Component/Form';
import Icone from 'Component/Icone';
import Imagem from 'Component/Imagem';
import Plano from 'Interface/Planos';
import { Erro } from 'Interface/Requisicao';
import { CartaoDeCredito } from 'Interface/Transacoes';
import Usuario from 'Interface/Usuarios';
import { login as loginAction } from 'Reducer/Autenticacao';
import {
  useAdicionarAssinaturaMutation,
  useCadastrarMutation,
  useLoginMutation,
  useRecuperarPlanosQuery,
} from 'Service';
import { useAppDispatch, useAppSelector } from 'Store';
import {
  BANDEIRAS,
  MASCARA_CPF,
  MASCARA_DATA_VENCIMENTO,
  MASCARA_TELEFONE,
} from 'View/constants';
import { errosParaLista } from 'View/functions';

const regras = object({
  nome: string().required('Obrigatório'),
  email: string().email('Inválido').required('Obrigatório'),
  senha: string()
    .min(6, 'A senha deve ter no mínimo 6 caracteres')
    .required('Obrigatório'),
  confirmar_senha: string()
    .required('Obrigatório')
    .test('igual', 'Deve ser igual a outra senha', (valor, contexto) => {
      if (valor) {
        return valor == contexto.parent.senha;
      }

      return true;
    }),
  cpf: string().required('Obrigatório'),
  telefone1: string().required('Obrigatório'),
});

const regrasDePagamento = object({
  nome: string()
    .required('Obrigatório')
    .max(26, 'O nome deve ser igual ao que consta em seu cartão'),
  numero: number().required('Obrigatório'),
  vencimento: string().required('Obrigatório'),
  codigo: number().required('Obrigatório'),
  bandeira: string().required('Obrigatório'),
});

const Adicionar = (): React.ReactElement => {
  const navegacao = useNavigate();
  const alerta = useAlerta();
  const dispatch = useAppDispatch();
  const usuario = useAppSelector((estado) => estado.autenticacao.usuario);
  const [etapa, setEtapa] = useState<number>(1);
  const [fadeOut, setFadeOut] = useState<boolean>(false);
  const [cadastrar, { isLoading: cadastrando }] = useCadastrarMutation();
  const [login] = useLoginMutation();
  const [adicionarAssinatura, { isLoading: pagando }] =
    useAdicionarAssinaturaMutation();

  const { data: { planos: [plano] } = { planos: [{} as Plano] } } =
    useRecuperarPlanosQuery({ limit: 1 });

  useEffect(() => {
    if (usuario) {
      navegacao('/intro');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (fadeOut) {
      setTimeout(() => {
        navegacao('/intro');
      }, 1500);
    }
  }, [fadeOut, navegacao]);

  const enviar = async (dados: Usuario): Promise<void> => {
    try {
      await cadastrar(dados).unwrap();

      const resposta = await login({
        email: dados.email,
        senha: dados.senha,
      }).unwrap();

      dispatch(loginAction(resposta));

      setEtapa(2);

      ReactGA.event('sign_up', { method: 'Estude+' });
      ReactGA.event('login', { method: 'Estude+' });
      ReactGA.event('add_to_cart', {
        currency: 'BRL',
        value: plano.valor,
        items: [
          {
            item_id: plano.id,
            item_name: plano.nome,
            price: plano.valor,
          },
        ],
      });
    } catch (erros) {
      alerta({
        titulo: 'Erro ao cadastrar',
        conteudo: errosParaLista(erros as Erro),
        tema: 'danger',
      });
    }
  };

  const pagar = async (dados: CartaoDeCredito): Promise<void> => {
    try {
      await adicionarAssinatura({
        plano_id: plano.id,
        cartao: { ...dados },
      }).unwrap();

      ReactGA.event('add_payment_info', {
        currency: 'BRL',
        value: plano.valor,
        payment_type: 'Cartão de crédito',
        items: [
          {
            item_id: plano.id,
            item_name: plano.nome,
            price: plano.valor,
          },
        ],
      });
      ReactGA.event('purchase', {
        currency: 'BRL',
        value: plano.valor,
        items: [
          {
            item_id: plano.id,
            item_name: plano.nome,
            price: plano.valor,
          },
        ],
      });

      setFadeOut(true);
    } catch (erros) {
      alerta({
        titulo: 'Erro ao realizar o cadastro da assinatura',
        conteudo: errosParaLista(erros as Erro),
        tema: 'danger',
      });
    }
  };

  return (
    <div
      className={[
        'bg-black',
        'animate__animated',
        !fadeOut ? 'animate__fadeIn' : 'animate__fadeOut',
      ].class()}
      style={{
        backgroundImage: 'url(/img/fundo-assinar.webp)',
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'right',
        marginTop: '-130px',
        paddingTop: '130px',
        minHeight: window.innerHeight,
      }}
    >
      <div className="container h-100 py-5">
        <div className="h-100 row justify-content-center align-items-center">
          {etapa == 1 ? (
            <div className="col-md-5">
              <BrowserView renderWithFragment>
                <p className="lead text-muted text-end">
                  São diversos cursos nas melhores área de atuação do mercado.
                  Com uma única assinatura, você tem acesso a mais de 1000
                  cursos com o suporte e confiança que você já conhece da
                  Faculdade Metropolitana do Estado de São Paulo –{' '}
                  <span className="text-primary fw-bold">
                    Nota Máxima no MEC!
                  </span>
                </p>
              </BrowserView>
              <p className="lead text-muted text-center text-sm-end">
                Ao cadastrar-se, você tem 7 dias de{' '}
                <span className="text-primary fw-bold">teste grátis</span>,
                sendo a primeira cobrança de{' '}
                <span className="text-secondary fw-bold">R$24,90</span> apenas
                após esse período! E mais: você pode cancelar a qualquer momento
                sem qualquer custo.
              </p>
              <div className="d-flex gap-3">
                <p className="lead text-muted text-end text-sm-end">
                  A Faculdade Metropolitana do Estado de São Paulo tem Selo de
                  Excelência no{' '}
                  <span className="text-primary">Reclame Aqui (RA1000)</span> e
                  possui Nota Máxima no MEC.
                </p>
                <a
                  href="https://www.reclameaqui.com.br/empresa/estude-sem-fronteiras/?utm_source=referral&utm_medium=embbed&utm_campaign=reputacao&utm_term=vertical"
                  className="border border-success rounded bg-dark p-2 d-flex flex-column align-items-center justify-content-center text-decoration-none"
                  target="_blank"
                  rel="noreferrer"
                >
                  <img
                    src="https://s3.amazonaws.com/raichu-beta/selos/assets/images/otimo.svg"
                    alt="Ótimo"
                  />
                  <span className="text-white">Ótimo</span>
                  <img
                    src="https://s3.amazonaws.com/raichu-beta/selos/assets/images/reclame-aqui-logo.svg"
                    alt="Selo Reclame Aqui"
                    style={{ height: 12.75 }}
                  />
                </a>
              </div>
              <BrowserView renderWithFragment>
                <p className="fs-3 text-white text-center text-sm-end mt-5 mb-2">
                  Estude+ nas redes sociais
                </p>
                <div className="d-flex justify-content-center justify-content-sm-end gap-3">
                  <a
                    href="https://www.facebook.com/estudemaiscursosonline/"
                    className="text-decoration-none text-muted fs-2"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Icone nome="facebook" />
                  </a>
                  <a
                    href="https://www.instagram.com/estudemaiscursosonline/"
                    className="text-decoration-none text-muted fs-2"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Icone nome="instagram" />
                  </a>
                  <a
                    href="https://www.linkedin.com/company/estude-mais-cursos-online/"
                    className="text-decoration-none text-muted fs-2"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Icone nome="linkedin" />
                  </a>
                </div>
              </BrowserView>
            </div>
          ) : (
            <div className="col-md-5">
              <h1 className="fw-lighter text-white mb-3 text-center text-sm-end">
                Quase lá....
              </h1>
              <p className="lead text-muted text-center text-sm-end">
                Para que seu cadastro seja validado, é necessário informar seus
                dados de cobrança. Esses dados são usados somente para validar
                seu cadastro, pois{' '}
                <span className="text-primary fw-bold">NENHUMA cobrança</span>{' '}
                ocorrerá nos primeiros{' '}
                <span className="text-secondary fw-bold">7 dias.</span>
              </p>
              <p className="lead text-muted text-center text-sm-end">
                Os dados informados ficam armazenados com nosso parceiro{' '}
                <span className="text-danger fw-bold">Santander</span>, via
                GetNet. Após os 7 Dias Grátis será debitado em seu cartão o
                valor de {plano.valor?.moeda()} a cada 30 dias, sem uso do
                limite.
              </p>
              <small className="d-block text-muted text-center text-sm-end">
                *Lembramos que você pode cancelar sua assinatura a qualquer
                momento e sem custo.
              </small>
              <div className="d-flex justify-content-center justify-content-sm-end mt-3">
                <Imagem
                  src="santander.webp"
                  alt="Pague com segurança: Santander"
                  className="rounded"
                  height={50}
                />
              </div>
            </div>
          )}
          <div className="col-xl-6 col-xxl-5 mt-5 mt-sm-3 mt-sm-0 px-4">
            <div className="w-100 d-flex justify-content-center align-items-center flex-column">
              <div className="bg-white p-4 p-sm-5 rounded w-100">
                {etapa == 1 && (
                  <Form onSubmit={enviar} regras={regras}>
                    <h1 className="mb-4">Seus dados</h1>
                    <Campo name="nome" type="text" rotulo="Nome completo" />
                    <Campo
                      name="email"
                      type="email"
                      rotulo="E-mail"
                      prefixar={<Icone nome="envelope-fill" />}
                    />
                    <div className="row">
                      <div className="col-md-6">
                        <Campo
                          name="senha"
                          type="password"
                          rotulo="Senha"
                          prefixar={<Icone nome="lock-fill" />}
                        />
                      </div>
                      <div className="col-md-6">
                        <Campo
                          name="confirmar_senha"
                          type="password"
                          rotulo="Confirmar senha"
                          prefixar={<Icone nome="lock-fill" />}
                        />
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-md-6">
                        <Campo
                          name="cpf"
                          type="text"
                          rotulo="Número do CPF"
                          mascara={MASCARA_CPF}
                        />
                      </div>
                      <div className="col-md-6">
                        <Campo
                          name="telefone1"
                          type="tel"
                          rotulo="Celular"
                          prefixar={<Icone nome="telephone-fill" />}
                          mascara={MASCARA_TELEFONE}
                        />
                      </div>
                    </div>

                    <Botao
                      lg
                      type="submit"
                      className="w-100"
                      processando={cadastrando}
                      texto="Cadastrar"
                      textoEmProcessamento="Cadastro seu usuário..."
                    />

                    <p className="mt-3 mb-0">
                      Já possui uma conta?
                      <Link
                        to="/login"
                        className="fw-bold text-decoration-none ms-2"
                      >
                        Faça login!
                      </Link>
                    </p>
                  </Form>
                )}
                {etapa == 2 && (
                  <Form onSubmit={pagar} regras={regrasDePagamento}>
                    <h1 className="mb-4">Dados do seu cartão</h1>
                    <Selecao
                      name="bandeira"
                      rotulo="Bandeira do cartão"
                      opcoes={BANDEIRAS}
                    />
                    <Campo name="nome" rotulo="Nome como consta no cartão" />
                    <Campo name="numero" rotulo="Número do cartão" />
                    <div className="row">
                      <div className="col-6">
                        <Campo
                          name="vencimento"
                          rotulo="Dt. vencimento"
                          mascara={MASCARA_DATA_VENCIMENTO}
                          semMargem
                        />
                      </div>
                      <div className="col-6">
                        <Campo
                          name="codigo"
                          rotulo="Cód. segurança"
                          semMargem
                        />
                      </div>
                    </div>
                    <div className="mt-3 d-flex">
                      <Botao
                        lg
                        type="submit"
                        className="w-100"
                        processando={pagando}
                        texto="Experimentar 7 Dias Grátis"
                        textoEmProcessamento="Finalizando cadastro..."
                      />
                    </div>
                  </Form>
                )}
              </div>
            </div>
          </div>
          <BrowserView renderWithFragment>
            <div className="col-md-12 mt-5">
              <p className="text-center text-muted mb-0">
                O <strong className="text-primary">Estude+</strong> é um serviço
                de assinatura da{' '}
                <strong className="text-secondary">
                  Faculdade Metropolitana
                </strong>{' '}
                que provê educação para todos por um{' '}
                <strong>preço acessível</strong>.
              </p>
              <p className="text-center text-muted">
                <small>
                  Quer conhecer mais? Crie já sua conta e acesse o Estude+!
                </small>
              </p>
            </div>
          </BrowserView>
        </div>
      </div>
    </div>
  );
};

export default Adicionar;
